import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TCaseFilter } from "src/aadeCase/data-access/types";
import useGetAdminBasicFilters from "src/aadeCase/hooks/useGetAdminBasicFilters";
import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { FieldValues } from "react-hook-form";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import i18n from "src/shared/services/languages/i18n";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import useGetAdminPastAadeCases from "src/aadeCase/data-access/useGetAdminPastAadeCases";

export default function AdminPastAadeCases() {
  const { t } = useTranslation();
  const filters: TCaseFilter[] = useGetAdminBasicFilters();
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);
  const [searchFilters, setSearchFilters] = useState({});

  const adminPastAadeCaseFilters: TCaseFilter[] = [
    ...filters,
    {
      label: t("Διεκπεραίωση υπόθεσης από"),
      component: { type: "datePicker" },
      name: "completed_date_from",
    },
    {
      label: t("Διεκπεραίωση υπόθεσης έως"),
      component: { type: "datePicker" },
      name: "completed_date_to",
    },
  ];

  const {
    data: adminPastAadeCasesResponse,
    isLoading: isLoadingAdminPastAadeCases,
  } = useGetAdminPastAadeCases({
    searchFilters,
    enableSearch,
    setEnableSearch,
    paginationState,
  });

  const handleSearch = (data: FieldValues) => {
    console.log("Search data:", data);
    setSearchFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
      chamber: data.chamber ? data.chamber.value : null,
      completed_date_from: data.completed_date_from
        ? format(data.completed_date_from, "yyyy-MM-dd")
        : null,
      completed_date_to: data.completed_date_to
        ? format(data.completed_date_to, "yyyy-MM-dd")
        : null,
    });
    setEnableSearch(true);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("Αριθμός υπόθεσης"),
        accessorKey: "case_id",
        accessorFn: (originalRow) => {
          return (
            <Link
              to={`${"/admin/manageAadeCaseApplication"}/${originalRow.case_id}`}
            >
              {originalRow.case_id}
            </Link>
          );
        },
      },
      {
        header: t("Αριθμός Γ.Ε.ΜΗ."),
        accessorKey: "company_id",
      },
      {
        header: t("ΑΦΜ"),
        accessorKey: "afm",
      },
      {
        header: t("Νομική Μορφή"),
        accessorKey: "legalType",
      },
      {
        header: t("Πλήθος αναντιστοιχιών"),
        accessorKey: "subcase_count",
      },
      {
        header: t("Ημερομηνία Δημιουργίας"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.date_created),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
      {
        header: t("Ημερομηνία Διεκπεραίωσης"),
        accessorKey: "completed_date",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.completed_date),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
      {
        header: t("Επιμελητήριο"),
        accessorKey: "chamber",
      },
    ],
    [i18n.language],
  );

  const table = useCustomMaterialTable({
    columns,
    data: adminPastAadeCasesResponse?.data.rows ?? [],
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    localization: getTableLang(),
    rowCount: adminPastAadeCasesResponse?.data.rowCount ?? 0,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoadingAdminPastAadeCases,
      showProgressBars: isLoadingAdminPastAadeCases,
      pagination: paginationState,
    },
    manualPagination: true,
  });

  return (
    <AadeCaseSearchOrchestrator
      title={t("Διεκπεραιωμένες Υποθέσεις Εκκαθάρισης")}
      subtitle={t(
        "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν διεκπεραιωθεί από υπάλληλο της Υ. Γ.Ε.ΜΗ.",
      )}
      filters={adminPastAadeCaseFilters}
      handleSearch={handleSearch}
      table={table}
    />
  );
}
